import { useCallback, useEffect, useState } from 'react';

import { useTracking } from 'hooks/analytic-tracking';
import { useReportingTrends } from 'hooks/use-reporting-trends';
import { Badge } from 'react-bootstrap';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'components/FormComponents';
import { Table } from 'components/Table/Table';

import {
  useGetCandidatesFiltersQuery,
  useLazyDownloadCandidatesReportQuery,
  useLazyGetCandidatesReportingQuery,
  useLazyGetCandidatesStatsQuery,
  useLazyGetCandidatesTrendsQuery,
} from 'api/reporting-api';
import { selectUser } from 'api/user-slice';

import { ReportingChart } from '../../components/ReportingChart/ReportingChart';
import { ReportingWidget } from '../../components/ReportingWidget/ReportingWidget';

const CANDIDATES_SERIES = [
  { key: 'completed', label: 'Completed', color: '#039d41' },
  { key: 'in_progress', label: 'In-Progress', color: '#2f80ed' },
  { key: 'overdue', label: 'Overdue', color: '#ef4444' },
  { key: 'candidate', label: 'Candidate', color: '#dfbf0b' },
  { key: 'canceled', label: 'Cancelled', color: '#5c4d78' }, // $neutral-06
];

export const CandidatesReporting = ({ locale, setLoading = () => {} }) => {
  const [getCandidatesStats] = useLazyGetCandidatesStatsQuery();
  const [getCandidatesTrends] = useLazyGetCandidatesTrendsQuery();
  const { data: filters } = useGetCandidatesFiltersQuery();
  const [getCandidatesReporting] = useLazyGetCandidatesReportingQuery();
  const [downloadCandidatesReport] = useLazyDownloadCandidatesReportQuery();

  const user = useSelector(selectUser);
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState({});
  const [queryParams, setQueryParams] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const trendsFetcher = useCallback(
    async params => getCandidatesTrends(params).unwrap(),
    [getCandidatesTrends],
  );
  const {
    trends,
    groupBy,
    datePreset,
    datePresets,
    fetchTrends,
    handleGroupByChange,
    handleDatePresetChange,
  } = useReportingTrends(trendsFetcher);

  const fetchCandidates = async (params = {}) => {
    setLoading(true);
    setQueryParams(params);
    const result = await getCandidatesReporting(params).unwrap();
    const stats = await getCandidatesStats(params).unwrap();
    setStats(stats);
    setPagination(result.pagination);
    setCandidates(result.candidates);
    try {
      await fetchTrends(params);
    } catch {
      // Trends failed; table and stats are already loaded, don't block UI
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  const download = async () => {
    setLoading(true);
    await downloadCandidatesReport({ queryParams }).unwrap();
    setLoading(false);
  };

  const filteredSeries = queryParams.status?.length
    ? CANDIDATES_SERIES.filter(s => queryParams.status.includes(s.key.replace(/_/g, '-')))
    : CANDIDATES_SERIES;

  return (
    <Table.Context onQueryUpdate={fetchCandidates} defaultQuery={{ page: 1 }}>
      <Table.FloatingFilters className="u-margin-bottom--large surface-white">
        <Table.FloatingFiltersLeft>
          <Table.DateRangeFilter title="Created within" filterName="created_at" />
          <Table.DateRangeFilter title="Completed within" filterName="completed_at" />
          <Table.MultiSelectFilter
            title="Status"
            filterName="status"
            type="status"
            useDefault={false}
            options={[
              { name: 'Candidate', value: 'candidate' },
              { name: 'In-Progress', value: 'in-progress' },
              { name: 'Completed', value: 'completed' },
              { name: 'Overdue', value: 'overdue' },
              { name: 'Canceled', value: 'canceled' },
            ]}
          />
          <Table.MultiSelectFilter
            title="Archived"
            filterName="archived"
            type="status"
            useDefault={false}
            options={[
              { name: 'Yes', value: true },
              { name: 'No', value: false },
            ]}
          />
          <Table.MultiSelectFilter
            title="Team"
            filterName="team"
            type="status"
            useDefault={false}
            options={filters?.teams}
          />
          <Table.MultiSelectFilter
            title="Brand"
            filterName="brand"
            type="status"
            useDefault={false}
            useSearchInMultiSelect={true}
            options={filters?.brands}
          />
          <Table.MultiSelectFilter
            title="User"
            filterName="user_id"
            type="status"
            useDefault={false}
            useSearchInMultiSelect={true}
            options={[...(filters?.users || [])].sort((a, b) => a.name.localeCompare(b.name))}
          />
          <Table.MultiSelectFilter
            title="Bundle"
            filterName="bundle"
            type="status"
            useDefault={false}
            useSearchInMultiSelect={true}
            options={filters?.bundles}
          />
        </Table.FloatingFiltersLeft>
        <Table.FloatingFiltersRight>
          <Button icon="download" onClick={download}>
            Export
          </Button>
        </Table.FloatingFiltersRight>
      </Table.FloatingFilters>
      <ReportingWidget>
        <ReportingWidget.Tile>
          <ReportingWidget.Badge>Total Candidates</ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.total_candidates}</ReportingWidget.Value>
          <ReportingWidget.Label></ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile>
          <ReportingWidget.Badge>Average Turnaround</ReportingWidget.Badge>
          <ReportingWidget.Value>
            {stats?.avg_turnaround_time}
            <span className="title-3">h</span>
          </ReportingWidget.Value>
          <ReportingWidget.Label></ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Divider />
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Candidate}
          disabled={queryParams.status && !queryParams.status.includes('candidate')}>
          <ReportingWidget.Badge>
            Candidate (
            {stats.total_candidates == 0
              ? 0
              : Math.round((stats?.candidate * 100) / stats.total_candidates)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.candidate}</ReportingWidget.Value>
          <ReportingWidget.Label>Candidates</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.InProgress}
          disabled={queryParams.status && !queryParams.status.includes('in-progress')}>
          <ReportingWidget.Badge>
            In-progress (
            {stats.total_candidates == 0
              ? 0
              : Math.round((stats?.in_progress * 100) / stats.total_candidates)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.in_progress}</ReportingWidget.Value>
          <ReportingWidget.Label>Candidates</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Overdue}
          disabled={queryParams.status && !queryParams.status.includes('overdue')}>
          <ReportingWidget.Badge>
            Overdue (
            {stats.total_candidates == 0
              ? 0
              : Math.round((stats?.overdue * 100) / stats.total_candidates)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.overdue}</ReportingWidget.Value>
          <ReportingWidget.Label>Candidates</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Completed}
          disabled={queryParams.status && !queryParams.status.includes('completed')}>
          <ReportingWidget.Badge>
            Complete (
            {stats.total_candidates == 0
              ? 0
              : Math.round((stats?.completed * 100) / stats.total_candidates)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.completed}</ReportingWidget.Value>
          <ReportingWidget.Label>Candidates</ReportingWidget.Label>
        </ReportingWidget.Tile>
        <ReportingWidget.Tile
          type={ReportingWidget.Types.Canceled}
          disabled={queryParams.status && !queryParams.status.includes('canceled')}>
          <ReportingWidget.Badge>
            Cancelled (
            {stats.total_candidates == 0
              ? 0
              : Math.round((stats?.canceled * 100) / stats.total_candidates)}
            %)
          </ReportingWidget.Badge>
          <ReportingWidget.Value>{stats?.canceled}</ReportingWidget.Value>
          <ReportingWidget.Label>Candidates</ReportingWidget.Label>
        </ReportingWidget.Tile>
      </ReportingWidget>
      <ReportingChart
        data={trends}
        series={filteredSeries}
        groupBy={groupBy}
        onGroupByChange={newGroupBy => handleGroupByChange(newGroupBy, queryParams)}
        datePreset={datePreset}
        datePresets={datePresets}
        onDatePresetChange={presetKey => handleDatePresetChange(presetKey, queryParams)}
      />
      <Table.TableContent>
        <Table.Filters>
          <Table.Search placeholder="Search by candidate name..." />
        </Table.Filters>
        <Table className="u-margin--large">
          <Table.Head>
            <Table.Row>
              <Table.Header sortKey="candidates.first_name">Name</Table.Header>
              <Table.Header sortKey="candidates.status">Status</Table.Header>
              <Table.Header sortKey="candidates.email">Email</Table.Header>
              <Table.Header sortKey="candidates.bundle_id">Bundle</Table.Header>
              <Table.Header sortKey="candidates.created_at">Created</Table.Header>
              <Table.Header sortKey="candidates.submitted_at">Submitted</Table.Header>
              <Table.Header sortKey="candidates.completed_at">Completed</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {candidates.map(candidate => (
              <Table.Row key={candidate.id}>
                <Table.Col>
                  <Link className="u-link dark" to={`/check/${candidate.id}`}>
                    {candidate.first_name} {candidate.last_name}
                  </Link>
                </Table.Col>
                <Table.Col>
                  <Badge className={`u-status-${candidate.status}`}>{candidate.status}</Badge>
                </Table.Col>
                <Table.Col>{candidate.email}</Table.Col>
                <Table.Col>
                  {candidate.bundle_id
                    ? filters?.bundles.find(bundle => bundle.value == candidate.bundle_id)?.name
                    : '-'}
                </Table.Col>
                <Table.Col>
                  <Moment format={locale.dateSlashFormat}>{candidate.created_at}</Moment>
                </Table.Col>
                <Table.Col>
                  {candidate.submitted_at ? (
                    <Moment format={locale.dateSlashFormat}>{candidate.submitted_at}</Moment>
                  ) : (
                    '-'
                  )}
                </Table.Col>
                <Table.Col>
                  {candidate.completed_at ? (
                    <Moment format={locale.dateSlashFormat}>{candidate.completed_at}</Moment>
                  ) : (
                    '-'
                  )}
                </Table.Col>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Pagination {...pagination} />
        </Table>
      </Table.TableContent>
    </Table.Context>
  );
};
